import { Check, Mail } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState, type FormEvent } from 'react'
import { Reveal } from '../components/hareket'
import { Baslik, Buton, Kart, SayfaUstu, Section, WHATSAPP_URL, cn } from '../components/ui'
import { useHead } from '../lib/head'

const EPOSTA = 'merhaba@akillidernek.com'
const UC: string | undefined = import.meta.env.VITE_LEAD_ENDPOINT

type Durum = 'bos' | 'gonderiliyor' | 'tamam' | 'hata'

export function Iletisim() {
  useHead(
    {
      title: 'Demo ve iletişim',
      description: 'Demo isteyin ya da deneme hesabı açtırın. İş günlerinde aynı gün dönüyoruz.',
    },
    '/iletisim',
  )
  const [durum, setDurum] = useState<Durum>('bos')

  async function gonder(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const veri = Object.fromEntries(new FormData(form).entries()) as Record<string, string>
    if (veri.web) return // bal küpü: botlar doldurur, insanlar görmez
    setDurum('gonderiliyor')
    if (!UC) {
      // Toplama ucu tanımlı değilse e-posta istemcisine düş; içerik kaybolmasın.
      const govde = Object.entries(veri)
        .filter(([k]) => k !== 'web')
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n')
      window.location.href = `mailto:${EPOSTA}?subject=${encodeURIComponent('Demo talebi · ' + veri.dernek)}&body=${encodeURIComponent(govde)}`
      setDurum('tamam')
      return
    }
    try {
      const r = await fetch(UC, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...veri, kaynak: 'www', sayfa: window.location.pathname }),
      })
      setDurum(r.ok ? 'tamam' : 'hata')
    } catch {
      setDurum('hata')
    }
  }

  const alan =
    'h-11 w-full rounded-[var(--radius-control)] bg-surface px-3 text-sm text-ink ring-1 ring-line placeholder:text-ink-3 focus:ring-2 focus:ring-brand focus:outline-none'

  return (
    <SayfaUstu>
      <Section className="pt-0 sm:pt-0">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
          <Reveal>
            <Baslik
              seviye="h1"
              eyebrow="Demo ve deneme"
              baslik="Yarım saatte gösterelim"
              metin="Derneğinizin işine göre bir demo yapalım ya da doğrudan deneme hesabınızı açalım. İş günlerinde aynı gün dönüyoruz; formu doldurmak iki dakika sürer."
            />
            <ul className="mt-8 space-y-3 text-[15px] text-ink">
              {[
                'Kart bilgisi istemiyoruz',
                'Elli üyeye kadar on dört gün deneme',
                'Excel listenizi görüşmede birlikte aktarabiliriz',
              ].map((m) => (
                <li key={m} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
                  {m}
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-2 text-sm text-ink-2">
              <p className="flex items-center gap-2">
                <Mail className="size-4 text-brand" aria-hidden />
                <a href={`mailto:${EPOSTA}`} className="hover:text-ink">
                  {EPOSTA}
                </a>
              </p>
              {WHATSAPP_URL && (
                <p>
                  <a href={WHATSAPP_URL} rel="noreferrer" className="text-brand hover:underline">
                    WhatsApp ile yazın
                  </a>
                </p>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Kart className="p-6 sm:p-8">
              <AnimatePresence mode="wait">
                {durum === 'tamam' ? (
                  <motion.div
                    key="tamam"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-8 text-center"
                  >
                    <span className="mx-auto grid size-12 place-items-center rounded-full bg-brand-subtle text-brand">
                      <Check className="size-6" />
                    </span>
                    <p className="mt-4 text-lg font-semibold text-ink">Talebiniz alındı</p>
                    <p className="mt-2 text-sm text-ink-2">
                      İş günlerinde aynı gün, yazdığınız e-posta adresinden dönüyoruz.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={gonder}
                    className="grid gap-4"
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="text-sm">
                        <span className="mb-1.5 block font-medium text-ink">Adınız</span>
                        <input name="ad" required autoComplete="name" className={alan} />
                      </label>
                      <label className="text-sm">
                        <span className="mb-1.5 block font-medium text-ink">E-posta</span>
                        <input
                          name="eposta"
                          type="email"
                          required
                          autoComplete="email"
                          className={alan}
                        />
                      </label>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="text-sm">
                        <span className="mb-1.5 block font-medium text-ink">Dernek</span>
                        <input
                          name="dernek"
                          required
                          autoComplete="organization"
                          className={alan}
                        />
                      </label>
                      <label className="text-sm">
                        <span className="mb-1.5 block font-medium text-ink">
                          Telefon <span className="font-normal text-ink-3">isteğe bağlı</span>
                        </span>
                        <input name="telefon" type="tel" autoComplete="tel" className={alan} />
                      </label>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="text-sm">
                        <span className="mb-1.5 block font-medium text-ink">
                          Tahmini üye sayısı
                        </span>
                        <select name="uye" className={alan} defaultValue="300">
                          <option value="50">50’ye kadar</option>
                          <option value="300">50–300</option>
                          <option value="1500">300–1.500</option>
                          <option value="10000">1.500–10.000</option>
                          <option value="10001">10.000 üzeri</option>
                        </select>
                      </label>
                      <label className="text-sm">
                        <span className="mb-1.5 block font-medium text-ink">Ne istersiniz</span>
                        <select name="konu" className={alan} defaultValue="demo">
                          <option value="demo">Demo görüşmesi</option>
                          <option value="deneme">Deneme hesabı</option>
                          <option value="tasima">Veri taşıma teklifi</option>
                          <option value="kurumsal">Kurumsal plan</option>
                        </select>
                      </label>
                    </div>
                    <label className="text-sm">
                      <span className="mb-1.5 block font-medium text-ink">
                        Kısaca{' '}
                        <span className="font-normal text-ink-3">
                          bugün ne kullanıyorsunuz, sizi en çok ne yoruyor
                        </span>
                      </span>
                      <textarea name="mesaj" rows={4} className={cn(alan, 'h-auto py-2.5')} />
                    </label>
                    <label className="hidden" aria-hidden>
                      Web
                      <input name="web" tabIndex={-1} autoComplete="off" />
                    </label>
                    <p className="text-xs leading-relaxed text-ink-3">
                      Bilgileriniz yalnızca talebinize dönmek için kullanılır; ayrıntı için{' '}
                      <a href="/kvkk" className="underline">
                        aydınlatma metni
                      </a>
                      .
                    </p>
                    {durum === 'hata' && (
                      <p className="text-sm text-danger">
                        Gönderilemedi. Doğrudan {EPOSTA} adresine yazabilirsiniz.
                      </p>
                    )}
                    <Buton type="submit" size="lg" ok disabled={durum === 'gonderiliyor'}>
                      {durum === 'gonderiliyor' ? 'Gönderiliyor' : 'Gönder'}
                    </Buton>
                  </motion.form>
                )}
              </AnimatePresence>
            </Kart>
          </Reveal>
        </div>
      </Section>
    </SayfaUstu>
  )
}
